<template>
  <div class="choose-frame" data-testid="choose-frame-tab">
    <div v-if="hasError" class="choose-frame-empty" data-testid="choose-frame-error">
      <Icon name="general.videoCamera" size="lg" class="empty-icon" />
      <p class="empty-message">Unable to load frames for this video</p>
    </div>

    <template v-else>
      <div class="preview-pane">
        <img
          v-if="selectedPreview"
          :src="selectedPreview.url"
          class="preview-image"
          data-testid="choose-frame-preview"
          :alt="`Frame at ${formattedSelectedTime}`"
        />
        <div v-else class="preview-skeleton animate-pulse" data-testid="choose-frame-preview-loading"></div>
        <span v-if="selectedPreview" class="preview-time" data-testid="choose-frame-preview-time">Preview: {{ formattedSelectedTime }}</span>
      </div>

      <div v-if="isLoading" class="filmstrip" data-testid="choose-frame-filmstrip-loading">
        <div v-for="index in FRAME_COUNT" :key="index" class="filmstrip-tile-skeleton animate-pulse"></div>
      </div>
      <div
        v-else
        ref="filmstripEl"
        class="filmstrip"
        data-testid="choose-frame-filmstrip"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
      >
        <img
          v-for="preview in previews"
          :key="preview.timestamp"
          :src="preview.url"
          class="filmstrip-tile"
          :class="{ 'filmstrip-tile-selected': preview.timestamp === selectedTimestamp }"
          :data-testid="`choose-frame-tile-${preview.timestamp}`"
          :alt="`Frame at ${preview.timestamp}s`"
          draggable="false"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Icon } from '@/storybook';
import { FRAME_COUNT, useChooseFrame } from '@/modules/viewer/composables/useChooseFrame';

interface Props {
  itemId: string;
  contentUrl: string;
  currentTimestamp?: number | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{ select: [timestamp: number] }>();

const { previews, isLoading, hasError, selectedTimestamp, selectedPreview, selectFrame } = useChooseFrame({
  itemId: props.itemId,
  contentUrl: props.contentUrl,
  currentTimestamp: props.currentTimestamp,
});

const filmstripEl = ref<HTMLElement | null>(null);
const isDragging = ref(false);

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formattedSelectedTime = computed(() => (selectedPreview.value ? formatTime(selectedPreview.value.timestamp) : ''));

// Every selection change is emitted, including the composable's initial default —
// the modal's Upload button reads this to know a frame is already selected on tab open.
watch(
  selectedTimestamp,
  timestamp => {
    if (timestamp != null) {
      emit('select', timestamp);
    }
  },
  { immediate: true }
);

const selectFromPointer = (clientX: number) => {
  const el = filmstripEl.value;
  if (!el || previews.value.length === 0) {
    return;
  }

  const rect = el.getBoundingClientRect();
  const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  const index = Math.round(ratio * (previews.value.length - 1));
  const preview = previews.value[index];
  if (preview) {
    selectFrame(preview.timestamp);
  }
};

const onPointerDown = (event: PointerEvent) => {
  isDragging.value = true;
  (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  selectFromPointer(event.clientX);
};

const onPointerMove = (event: PointerEvent) => {
  if (!isDragging.value) {
    return;
  }
  selectFromPointer(event.clientX);
};

const onPointerUp = () => {
  isDragging.value = false;
};
</script>

<style scoped>
.choose-frame {
  @apply flex flex-col gap-4;
}

.choose-frame-empty {
  @apply flex flex-col items-center justify-center h-52 text-center px-4;
}

.empty-icon {
  @apply text-zinc-300 dark:text-zinc-600 mb-3;
}

.empty-message {
  @apply text-zinc-500 dark:text-zinc-400;
}

.preview-pane {
  @apply relative flex items-center justify-center h-52
         bg-zinc-100 dark:bg-zinc-800 rounded-lg overflow-hidden;
}

.preview-image {
  @apply w-full h-full object-cover;
}

.preview-skeleton {
  @apply w-full h-full bg-zinc-200 dark:bg-zinc-700;
}

.preview-time {
  @apply absolute bottom-2 left-2 px-2 py-0.5 rounded-md
         text-xs font-medium text-white bg-black/60;
}

.filmstrip {
  /* Shorter than the preview pane on purpose: with a fixed modal width and
     no horizontal scroll, each tile's box width is capped by FRAME_COUNT —
     a shorter track keeps the box closer to the source's 16:9 ratio, so
     object-cover crops less of each frame's width away. */
  @apply relative flex h-10 w-full rounded-md overflow-hidden
         cursor-pointer select-none touch-none;
}

.filmstrip-tile-skeleton {
  @apply flex-1 min-w-0 h-full bg-zinc-200 dark:bg-zinc-700;
}

.filmstrip-tile {
  @apply flex-1 min-w-0 h-full object-cover pointer-events-none
         opacity-60 transition-opacity;
}

.filmstrip-tile-selected {
  @apply opacity-100 relative z-10 rounded-md border-[3px] border-blue-500;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
